import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ChevronLeft, ChevronRight, ArrowUpRight, Check, Plus, Download, Sparkles,
  Mail, Loader2, AlertCircle,
} from 'lucide-react';
import { applyRecordFilters, sortRecords } from '../utils/recordFilters';
import { apiFetch } from '../api';
import { getRiskLevel } from '../utils/risk';
import { getDateFilterLabel, formatShortDate, stripMarkdown } from '../utils/format';
import { groupDuplicateStories } from '../utils/storyGrouping';
import { composeExecutiveBrief, exportExecutiveBriefPdf, sendExecutiveBriefToTeam } from '../utils/executiveBrief';
import { exportAlertsToPdf } from '../utils/alertExport';
import { displayHeadline } from '../utils/displayHeadline';
import CompanyLogo from './CompanyLogo';

const IST_DATE_FORMATTER = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Asia/Kolkata',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

function istDateKey(value) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const parts = Object.fromEntries(
    IST_DATE_FORMATTER.formatToParts(date)
      .filter(part => part.type !== 'literal')
      .map(part => [part.type, part.value])
  );
  return `${parts.year}-${parts.month}-${parts.day}`;
}

function addDaysToKey(dateKey, offsetDays) {
  const [year, month, day] = dateKey.split('-').map(Number);
  const utcNoon = new Date(Date.UTC(year, month - 1, day + offsetDays, 12, 0, 0));
  return istDateKey(utcNoon);
}

// Oldest → newest, ending today (IST).
function lastSevenDayKeys() {
  const today = istDateKey(new Date());
  return [6, 5, 4, 3, 2, 1, 0].map(offset => addDaysToKey(today, -offset));
}

const SEVERITY_RANK = { CRITICAL: 4, HIGH: 3, MEDIUM: 2, LOW: 1 };

function severityRank(record) {
  return SEVERITY_RANK[String(record?.severity || '').toUpperCase()] || 0;
}

function recordTime(record) {
  const value = record?.published_at || record?.fetched_at;
  const time = value ? new Date(value).getTime() : 0;
  return Number.isNaN(time) ? 0 : time;
}

function bySeverityThenNewest(a, b) {
  return severityRank(b) - severityRank(a) || recordTime(b) - recordTime(a);
}

function groupStoriesByCompany(storyGroups) {
  const byCompany = new Map();
  storyGroups.forEach(group => {
    const company = group.primary?.company || 'Unknown company';
    if (!byCompany.has(company)) byCompany.set(company, []);
    byCompany.get(company).push(group);
  });

  return [...byCompany.entries()]
    .map(([company, stories]) => {
      const sorted = [...stories].sort((a, b) => bySeverityThenNewest(a.primary, b.primary));
      return {
        company,
        top: sorted[0].primary,
        alertCount: stories.reduce((sum, story) => sum + 1 + (story.related?.length || 0), 0),
      };
    })
    .sort((a, b) => bySeverityThenNewest(a.top, b.top));
}

function sourceLabel(record) {
  if (record?.source) return record.source;
  try {
    return new URL(record.url).hostname.replace(/^www\./, '');
  } catch {
    return 'Source';
  }
}

const RISK_PILL = {
  high: { label: 'High', cls: 'text-[#bd2f2b] bg-[#fae8e7]', bar: '#bd2f2b' },
  medium: { label: 'Medium', cls: 'text-[#86690c] bg-[#f7f0d2]', bar: '#c9a82b' },
  low: { label: 'Low', cls: 'text-[#117049] bg-[#e1f3ea]', bar: '#2f9e6b' },
  unknown: { label: 'Unrated', cls: 'text-[#4b6679] bg-[#e6edf2]', bar: '#94a3b8' },
};

function Pill({ severity }) {
  const style = RISK_PILL[getRiskLevel(severity)] || RISK_PILL.unknown;
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs leading-none font-semibold px-2 py-1.5 rounded whitespace-nowrap ${style.cls}`}>
      <i className="w-1.5 h-1.5 rounded-full bg-current" />
      {style.label}
    </span>
  );
}

function Spark({ values, color }) {
  const w = 72;
  const h = 26;
  const max = Math.max(...values, 1);
  const pts = values.map((v, i) => [
    (i * (w - 6)) / (values.length - 1) + 3,
    h - 4 - (v / max) * (h - 9),
  ]);
  const line = pts.map(p => `${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
  const last = pts[pts.length - 1];
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} fill="none" aria-hidden="true" className="flex-none">
      <polygon points={`3,${h - 2} ${line} ${w - 3},${h - 2}`} fill={color} opacity=".12" />
      <polyline points={line} stroke={color} strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={last[0]} cy={last[1]} r="2.6" fill={color} />
    </svg>
  );
}

const ASK_PROMPTS = [
  'What changed in the last 24 hours?',
  'Which companies need attention first?',
  'Summarize the high-risk alerts this week',
];

const CARD = 'bg-white border border-[#e1e5ec] rounded-lg';
const EYEBROW = 'text-xs font-medium text-[#5b6780]';
const BTN = 'inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-[#cbd3df] bg-white text-[13px] font-medium text-[#0d1524] whitespace-nowrap hover:bg-[#f6f7fa] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white transition';
const PILL = 'inline-flex items-center gap-1.5 h-8 px-3.5 rounded-full border border-[#cbd3df] bg-white text-[13px] font-medium text-[#0d1524] whitespace-nowrap hover:bg-[#f6f7fa] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white transition';
const BTN_PRI = 'inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-[#1c4ed8] text-[13px] font-medium text-white whitespace-nowrap hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed transition';

function StatCard({ label, value, valueClass = '', note, noteClass = 'text-[#76829a]', spark, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`${CARD} flex w-full min-h-[104px] items-end justify-between gap-2.5 px-4 py-3.5 text-left hover:border-[#cbd3df] hover:bg-[#f6f7fa] transition`}
    >
      <div>
        <small className="block text-[13px] font-medium text-[#45516a]">{label}</small>
        <b className={`block text-[30px] leading-[1.15] font-semibold tracking-[-0.03em] ${valueClass}`}>{value}</b>
        <span className={`text-xs ${noteClass}`}>{note}</span>
      </div>
      {spark}
    </button>
  );
}

function DashboardView({
  records,
  loading,
  companies,
  onOpenRecord,
  setView,
  dateFilter,
  setSeverityFilter,
  onAddCompany,
  onScheduleReport,
  onAskAi,
  onViewCompany,
  updatedWatchlistIds,
}) {
  const [priorities, setPriorities] = useState(null);
  const [spotIndex, setSpotIndex] = useState(0);

  const trackedCompanies = useMemo(
    () => new Set([
      ...(companies || []).map(c => c.name || c),
      ...records.filter(record => record.entity_type === 'person').map(record => record.company).filter(Boolean),
    ]),
    [companies, records],
  );

  const panelRecords = useMemo(() => sortRecords(
    applyRecordFilters(records, { dateFilter, trackedCompanies }),
    'newest',
  ), [records, dateFilter, trackedCompanies]);

  const companyGroups = useMemo(
    () => groupStoriesByCompany(groupDuplicateStories(panelRecords)),
    [panelRecords],
  );
  const latestByCompany = companyGroups.slice(0, 3);

  const highRisk = useMemo(
    () => panelRecords.filter(r => getRiskLevel(r.severity) === 'high').sort(bySeverityThenNewest),
    [panelRecords],
  );
  const highRiskCompanies = new Set(highRisk.map(r => r.company)).size;
  const spotlight = highRisk.length ? highRisk[spotIndex % highRisk.length] : null;

  const dayKeys = useMemo(lastSevenDayKeys, []);
  const dayCounts = useMemo(() => {
    const all = Object.fromEntries(dayKeys.map(k => [k, 0]));
    const high = { ...all };
    panelRecords.forEach(r => {
      const key = istDateKey(r.published_at);
      if (key in all) {
        all[key] += 1;
        if (getRiskLevel(r.severity) === 'high') high[key] += 1;
      }
    });
    return { all: dayKeys.map(k => all[k]), high: dayKeys.map(k => high[k]) };
  }, [panelRecords, dayKeys]);
  const dayMax = Math.max(...dayCounts.all, 1);

  const riskCounts = useMemo(() => {
    const counts = { high: 0, medium: 0, low: 0 };
    panelRecords.forEach(r => {
      const level = getRiskLevel(r.severity);
      if (level in counts) counts[level] += 1;
    });
    return counts;
  }, [panelRecords]);

  const topCategories = useMemo(() => {
    const counts = {};
    panelRecords.forEach(r => (r.categories || []).forEach(c => { counts[c] = (counts[c] || 0) + 1; }));
    return Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 3);
  }, [panelRecords]);

  const briefDays = useMemo(() => {
    if (dateFilter?.preset === 'today') return 1;
    if (dateFilter?.preset === 'yesterday') return 2;
    if (dateFilter?.preset === '30d') return 28;
    return 7;
  }, [dateFilter]);

  const loadPriorities = useCallback(async () => {
    try {
      setPriorities(await apiFetch(`/ai/priorities?days=${briefDays}`));
    } catch {
      setPriorities(null);
    }
  }, [briefDays]);

  useEffect(() => { loadPriorities(); }, [loadPriorities]);

  const canGenerateBrief = useMemo(
    () => composeExecutiveBrief({ priorities, records: panelRecords }).briefItems.length > 0,
    [priorities, panelRecords],
  );

  const handleGenerateBrief = useCallback(() => {
    const brief = composeExecutiveBrief({ priorities, records: panelRecords });
    if (brief.briefItems.length > 0) exportExecutiveBriefPdf(brief);
  }, [priorities, panelRecords]);

  // Send email: the same brief Generate brief turns into a PDF, mailed to the
  // saved recipients (the Briefs page's send path).
  const [sendState, setSendState] = useState('idle');
  const handleSendBrief = useCallback(async () => {
    if (sendState === 'sending') return;
    const brief = composeExecutiveBrief({ priorities, records: panelRecords });
    if (brief.briefItems.length === 0) return;
    setSendState('sending');
    try {
      const activity = await sendExecutiveBriefToTeam(brief);
      setSendState(activity.status === 'failed' ? 'failed' : 'sent');
    } catch {
      setSendState('failed');
    }
    setTimeout(() => setSendState('idle'), 3000);
  }, [priorities, panelRecords, sendState]);

  const handleExportReport = useCallback(() => {
    if (!panelRecords.length) return;
    exportAlertsToPdf(panelRecords, { filterLabel: getDateFilterLabel(dateFilter), grouped: true });
  }, [panelRecords, dateFilter]);

  const openByRisk = (level) => {
    setSeverityFilter?.(level);
    setView('intelligence');
  };

  const windowLabel = getDateFilterLabel(dateFilter);
  const empty = !loading && panelRecords.length === 0;
  const savedUpdates = updatedWatchlistIds?.size || 0;
  const riskTotal = riskCounts.high + riskCounts.medium + riskCounts.low;
  const catMax = topCategories.length ? topCategories[0][1] : 1;

  const welcome = loading
    ? 'Loading your alerts…'
    : empty
    ? 'No alerts yet.'
    : highRisk.length
      ? `${highRisk.length} high-risk ${highRisk.length === 1 ? 'alert needs' : 'alerts need'} attention.`
      : 'Nothing high-risk right now.';

  return (
    <div>
      {/* Page header */}
      <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
        <div>
          <h1 className="text-2xl leading-tight font-semibold tracking-tight text-[#0d1524]">
            {welcome}
          </h1>
          <p className="mt-1 text-sm text-[#45516a]">
            {loading ? 'Fetching the latest reporting' : `${panelRecords.length} alert${panelRecords.length === 1 ? '' : 's'} · ${windowLabel}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className={PILL}
            onClick={handleSendBrief}
            disabled={!canGenerateBrief || sendState === 'sending'}
            title={canGenerateBrief ? 'Email this brief to the saved recipients' : 'Nothing to send in this window'}
          >
            {sendState === 'sending' ? <><Loader2 size={14} className="animate-spin" />Sending…</>
              : sendState === 'sent' ? <><Check size={14} className="text-[#117049]" />Sent</>
                : sendState === 'failed' ? <><AlertCircle size={14} className="text-[#b42318]" />Failed</>
                  : <><Mail size={14} />Send email</>}
          </button>
          <button type="button" className={BTN_PRI} onClick={handleGenerateBrief} disabled={!canGenerateBrief}
            title={canGenerateBrief ? undefined : 'Nothing to summarise in this window'}>
            Generate brief
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-3.5 mb-4 grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Alerts"
          value={panelRecords.length}
          note={empty ? 'None in this window' : windowLabel}
          spark={<Spark values={dayCounts.all} color="#1c4ed8" />}
          onClick={() => setView('intelligence')}
        />
        <StatCard
          label="High risk"
          value={highRisk.length}
          valueClass="text-[#bd2f2b]"
          note={`across ${highRiskCompanies} compan${highRiskCompanies === 1 ? 'y' : 'ies'}`}
          spark={<Spark values={dayCounts.high} color="#bd2f2b" />}
          onClick={() => openByRisk('high')}
        />
        <StatCard
          label="Companies monitored"
          value={(companies || []).length}
          note="all coverage active"
          onClick={() => setView('companies')}
        />
        <StatCard
          label="Watchlist changes"
          value={savedUpdates}
          note={savedUpdates ? `${savedUpdates === 1 ? 'story' : 'stories'} you saved changed` : 'nothing changed since your last look'}
          onClick={() => setView('watchlist')}
        />
      </div>

      <div className="grid gap-4 items-start grid-cols-1 xl:grid-cols-[minmax(0,1.75fr)_minmax(0,1fr)]">
        {/* Left column */}
        <div className="flex flex-col gap-4 min-w-0">
          {spotlight ? (
            <div className={`${CARD} overflow-hidden`}>
              <div className="h-1 bg-[#bd2f2b]" />
              <div className="px-[22px] py-5">
                <div className="flex items-center justify-between gap-2.5 mb-4">
                  <span className="flex items-center gap-2 text-sm font-semibold text-[#bd2f2b]">
                    <i className="w-2 h-2 rounded-full bg-[#bd2f2b]" />Needs your attention
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="text-[13px] text-[#5b6780] mr-1">
                      {(spotIndex % highRisk.length) + 1} of {highRisk.length}
                    </span>
                    {highRisk.length > 1 && (
                      <>
                        <button type="button" aria-label="Previous alert" onClick={() => setSpotIndex(i => (i - 1 + highRisk.length) % highRisk.length)}
                          className="w-8 h-8 grid place-items-center rounded-md border border-[#cbd3df] bg-white text-[#45516a] hover:bg-[#f6f7fa]">
                          <ChevronLeft size={15} />
                        </button>
                        <button type="button" aria-label="Next alert" onClick={() => setSpotIndex(i => (i + 1) % highRisk.length)}
                          className="w-8 h-8 grid place-items-center rounded-md border border-[#cbd3df] bg-white text-[#45516a] hover:bg-[#f6f7fa]">
                          <ChevronRight size={15} />
                        </button>
                      </>
                    )}
                  </span>
                </div>

                <div className="flex flex-wrap items-center gap-2.5">
                  <CompanyLogo name={spotlight.company} size="md" />
                  <span className="text-[15px] font-semibold text-[#1c4ed8]">{spotlight.company}</span>
                  <Pill severity={spotlight.severity} />
                  {spotlight.categories?.[0] && (
                    <span className="text-xs font-medium text-[#45516a] bg-[#f6f7fa] border border-[#e1e5ec] rounded px-1.5 py-0.5 capitalize">
                      {spotlight.categories[0]}
                    </span>
                  )}
                </div>

                <h2
                  role="link"
                  tabIndex={0}
                  onClick={() => onOpenRecord(spotlight)}
                  onKeyDown={e => { if (e.key === 'Enter') onOpenRecord(spotlight); }}
                  className="mt-3.5 text-[22px] leading-[1.25] font-semibold tracking-[-0.015em] text-[#0d1524] cursor-pointer hover:text-[#1c4ed8] transition [text-wrap:balance]"
                >
                  {displayHeadline(spotlight)}
                </h2>
                <p className="mt-2.5 max-w-[68ch] text-sm leading-relaxed text-[#45516a] line-clamp-3">
                  {stripMarkdown(spotlight.summary || spotlight.snippet || spotlight.content || '') || 'Open the story for the full details.'}
                </p>

                <div className="mt-5 flex flex-wrap items-center justify-between gap-3 pt-4 border-t border-[#e1e5ec]">
                  <span className="text-[13px] text-[#5b6780]">
                    {sourceLabel(spotlight)}
                    {spotlight.published_at ? ` · ${formatShortDate(spotlight.published_at)}` : ''}
                  </span>
                  <span className="flex items-center gap-2">
                    <button type="button" className={BTN} onClick={() => onViewCompany?.(spotlight.company)}>
                      All {spotlight.company} stories
                    </button>
                    <button type="button" className={BTN} onClick={() => onOpenRecord(spotlight)}>
                      Read full story <ChevronRight size={14} />
                    </button>
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <div className={`${CARD} px-[22px] py-5`}>
              <span className={`${EYEBROW} flex items-center gap-[7px] !text-[#117049] mb-3`}>
                <Check size={14} />{empty ? 'No alerts yet' : 'All clear'}
              </span>
              <h2 className="mb-2.5 text-[24px] leading-[1.18] font-semibold tracking-[-0.02em]">
                {empty ? 'Nothing has been verified in this window.' : 'No high-risk developments in this window.'}
              </h2>
              <p className="max-w-[60ch] text-[#45516a]">
                {empty
                  ? 'This can happen on a quiet day. Try a longer date range from the header, or check that collection is running.'
                  : 'Lower-severity alerts are available in All stories.'}
              </p>
              <div className="mt-[18px]">
                <button type="button" className={BTN} onClick={() => setView('intelligence')}>Open All stories</button>
              </div>
            </div>
          )}

          <div className={CARD}>
            <div className="flex items-center justify-between px-[18px] pt-[18px]">
              <h3 className="flex items-center gap-2 text-[15px] font-semibold text-[#0d1524]">
                <span className="w-1 h-4 rounded-full bg-[#1c4ed8]" />
                Latest by company
              </h3>
              <button type="button" onClick={() => setView('intelligence')} className="text-[12.5px] text-[#1c4ed8] hover:underline">
                View all stories →
              </button>
            </div>
            <div className="mt-2.5">
              {loading ? (
                <div className="px-[18px] py-10 text-center text-[#45516a]">Loading…</div>
              ) : latestByCompany.length === 0 ? (
                <div className="px-5 py-10 text-center text-[#45516a]">No alerts in this period.</div>
              ) : latestByCompany.map(group => (
                <button
                  key={group.company}
                  type="button"
                  onClick={() => onOpenRecord(group.top)}
                  className="w-full grid grid-cols-[40px_minmax(0,1fr)_auto_18px] items-center gap-3 px-3.5 py-3 border-t border-[#e1e5ec] text-left hover:bg-[#f6f7fa] transition"
                >
                  <CompanyLogo name={group.company} size="md" />
                  <span className="min-w-0">
                    <span className="flex items-center gap-2">
                      <span className="text-[13.5px] font-semibold text-[#1c4ed8]">{group.company}</span>
                      <span className="text-xs font-medium text-[#45516a] bg-[#f6f7fa] border border-[#e1e5ec] rounded px-1.5 py-0.5">
                        {group.alertCount} {group.alertCount === 1 ? 'story' : 'stories'}
                      </span>
                    </span>
                    <b className="block my-1 text-sm font-semibold leading-snug text-[#0d1524]">{displayHeadline(group.top)}</b>
                    <small className="text-xs text-[#76829a]">
                      {sourceLabel(group.top)}
                      {group.top.published_at ? ` · ${formatShortDate(group.top.published_at)}` : ''}
                      {group.top.categories?.[0] ? <span className="capitalize">{` · ${group.top.categories[0]}`}</span> : null}
                    </small>
                  </span>
                  <Pill severity={group.top.severity} />
                  <ArrowUpRight size={14} className="text-[#76829a]" />
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <button type="button" className={`${BTN}`} onClick={onAddCompany}>
              <Plus size={14} />Add company
            </button>
            <button type="button" className={`${BTN}`} onClick={onScheduleReport}>
              Schedule report
            </button>
            <button type="button" className={`${BTN}`} onClick={handleExportReport} disabled={!panelRecords.length}>
              <Download size={14} />Export report
            </button>
          </div>
        </div>

        {/* Right column */}
        <div className="flex flex-col gap-4 min-w-0">
          <div className={`${CARD} p-[18px]`}>
            <div className="flex items-center justify-between">
              <h3 className="flex items-center gap-2 text-[15px] font-semibold text-[#0d1524]">
                <span className="w-1 h-4 rounded-full bg-[#1c4ed8]" />Risk mix
              </h3>
              <span className="text-[13px] text-[#5b6780]">{panelRecords.length} alerts</span>
            </div>
            {riskTotal === 0 ? (
              <p className="my-3.5 text-[12.5px] text-[#76829a]">No alerts to chart in this window.</p>
            ) : (
              <div className="flex h-2.5 gap-0.5 my-4 rounded overflow-hidden">
                {['high', 'medium', 'low'].filter(k => riskCounts[k]).map(k => (
                  <button
                    key={k}
                    type="button"
                    style={{ flex: riskCounts[k], background: RISK_PILL[k].bar }}
                    className="min-w-[6px]"
                    title={`${RISK_PILL[k].label}: ${riskCounts[k]}`}
                    aria-label={`${RISK_PILL[k].label}: ${riskCounts[k]}`}
                    onClick={() => openByRisk(k)}
                  />
                ))}
              </div>
            )}
            <div className="grid grid-cols-3 gap-2">
              {['high', 'medium', 'low'].map(k => (
                <button key={k} type="button" onClick={() => openByRisk(k)}
                  className="text-left rounded-md border border-[#e1e5ec] px-3 py-2 hover:bg-[#f6f7fa] transition">
                  <small className="flex items-center gap-1.5 text-[13px] font-medium text-[#45516a]">
                    <i className="w-2 h-2 rounded-full" style={{ background: RISK_PILL[k].bar }} />{RISK_PILL[k].label}
                  </small>
                  <b className="block text-[22px] leading-tight font-semibold tracking-[-0.02em] text-[#0d1524]">{riskCounts[k]}</b>
                  <span className="text-xs text-[#76829a]">{riskTotal ? Math.round((riskCounts[k] / riskTotal) * 100) : 0}%</span>
                </button>
              ))}
            </div>
            <div className="mt-5 mb-1.5 text-sm font-semibold text-[#0d1524]">Top topics</div>
            {topCategories.length === 0 ? (
              <small className="text-[#76829a]">None yet</small>
            ) : topCategories.map(([cat, n]) => (
              <div key={cat} className="grid grid-cols-[84px_1fr_28px] items-center gap-3 py-1.5 text-[13px]">
                <span className="capitalize truncate">{cat}</span>
                <i className="block h-1.5 rounded bg-[#f6f7fa] overflow-hidden">
                  <s className="block h-full rounded-[3px] bg-[#1c4ed8] no-underline" style={{ width: `${(n / catMax) * 100}%` }} />
                </i>
                <em className="text-[13px] font-semibold not-italic text-right text-[#0d1524]">{n}</em>
              </div>
            ))}
          </div>

          <div className={`${CARD} p-[18px]`}>
            <div className="flex items-center justify-between">
              <h3 className="flex items-center gap-2 text-[15px] font-semibold text-[#0d1524]">
                <span className="w-1 h-4 rounded-full bg-[#1c4ed8]" />Last 7 days
              </h3>
              <span className="text-[13px] text-[#5b6780]">{dayCounts.all.reduce((a, b) => a + b, 0)} alerts</span>
            </div>
            {dayCounts.all.every(n => n === 0) ? (
              <p className="mt-2.5 text-[12.5px] text-[#76829a]">No signal to plot yet.</p>
            ) : (
              <div className="flex items-end gap-2 h-[120px] mt-4">
                {dayKeys.map((key, i) => {
                  const isToday = i === dayKeys.length - 1;
                  const weekday = new Date(`${key}T12:00:00`).toLocaleDateString('en-GB', { weekday: 'short' });
                  return (
                    <div key={key} title={`${weekday} ${key}: ${dayCounts.all[i]} alert${dayCounts.all[i] === 1 ? '' : 's'}`}
                      className="flex-1 h-full flex flex-col justify-end items-center gap-1">
                      <span className={`text-xs font-semibold ${isToday ? 'text-[#1c4ed8]' : 'text-[#45516a]'}`}>{dayCounts.all[i]}</span>
                      <i
                        className={`block w-full rounded-t min-h-[3px] ${isToday ? 'bg-[#1c4ed8]' : 'bg-[#c5d3f6]'}`}
                        style={{ height: `${Math.max((dayCounts.all[i] / dayMax) * 78, 3)}%` }}
                      />
                      <small className={`text-xs ${isToday ? 'font-semibold text-[#1c4ed8]' : 'text-[#76829a]'}`}>{isToday ? 'Today' : weekday}</small>
                    </div>
                  );
                })}
              </div>
            )}
            <p className="mt-3 text-xs text-[#76829a]">
              Counts show how much was reported, not how likely a risk is.
            </p>
          </div>

          <div className={`${CARD} p-[18px]`}>
            <div className="flex items-center justify-between">
              <h3 className="flex items-center gap-2 text-[15px] font-semibold text-[#0d1524]">
                <span className="w-1 h-4 rounded-full bg-[#1c4ed8]" />Ask AI
              </h3>
              <Sparkles size={15} className="text-[#1c4ed8]" />
            </div>
            <p className="mt-1 text-[12.5px] text-[#76829a]">Answers come from your monitored coverage, with sources.</p>
            <div className="mt-2.5">
              {ASK_PROMPTS.map(prompt => (
                <button
                  key={prompt}
                  type="button"
                  onClick={() => onAskAi?.(prompt)}
                  className="flex w-full items-center justify-between gap-2 py-2 border-t border-[#e1e5ec] text-left text-[13px] text-[#0d1524] hover:text-[#1c4ed8] transition"
                >
                  {prompt}
                  <ChevronRight size={14} className="flex-none text-[#76829a]" />
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DashboardView;
